import { useState } from "react"

import { useIsFetching, useQueryClient } from "@tanstack/react-query"
import { clsx } from "clsx"

import {
  LIGHTREACH_NEXT_STEP_LABEL,
  LIGHTREACH_NO_APPLICATION_MESSAGE,
  LIGHTREACH_NO_STIPULATIONS_MESSAGE,
  LIGHTREACH_REAPPLY_LABEL,
  LIGHTREACH_REFRESH_STATUS_ERROR,
  LIGHTREACH_REFRESH_STATUS_LABEL,
  LIGHTREACH_REFRESHING_STATUS_LABEL,
  LIGHTREACH_STIPULATIONS_HEADING,
  lightReachApplicationOutcome,
  lightReachStipulationState,
} from "./lightreach-application-copy"
import type {
  LightReachApplicationBundle,
  LightReachApplicationView,
  LightReachFinancingView,
} from "./lightreach-kind-bundle"
import { LightReachContract } from "./LightReachContract"
import {
  TPO_PRODUCT_TYPE_LABEL,
  tpoTermsText,
} from "../../../BatteryOnlyProposal/tpo-terms-copy"
import { OUTCOME_TONE_CLASS } from "../concert/concert-application-copy"

const sectionClass = `rounded-lg border border-bp-border bg-bp-elevated`
const badgeClass = `inline-flex items-center rounded-full border px-2.5 py-0.5 text-[length:var(--step--2)] font-semibold`

const dateLabel = (iso: string): string =>
  new Intl.DateTimeFormat(`en-US`, {
    year: `numeric`,
    month: `short`,
    day: `numeric`,
  }).format(new Date(iso))

/** LightReach states a calendar date, stored as UTC midnight. */
const calendarDateLabel = (iso: string): string =>
  new Intl.DateTimeFormat(`en-US`, {
    year: `numeric`,
    month: `short`,
    day: `numeric`,
    timeZone: `UTC`,
  }).format(new Date(iso))

/** A kind without a Refresh status command re-runs its financing read instead. */
const useNoRefreshCommand = (): null => null

const lightReachApplications = (
  financing: LightReachFinancingView,
): LightReachApplicationView[] =>
  [financing.currentApplication, ...financing.previousApplications].filter(
    (application): application is LightReachApplicationView =>
      application?.provider === `lightreach`,
  )

function Stipulations({
  testIdPrefix,
  application,
}: {
  testIdPrefix: string
  application: LightReachApplicationView
}): JSX.Element {
  const stipulations = application.stipulations ?? []
  return (
    <section
      aria-label={LIGHTREACH_STIPULATIONS_HEADING}
      data-testid={`${testIdPrefix}-lightreach-stipulations`}
      className={sectionClass}
    >
      <h3 className="border-b border-bp-border px-4 py-3 text-[length:var(--step--1)] font-semibold text-bp-text">
        {LIGHTREACH_STIPULATIONS_HEADING}
      </h3>
      {stipulations.length === 0 ? (
        <p className="px-4 py-3 text-[length:var(--step--2)] text-bp-text-muted">
          {LIGHTREACH_NO_STIPULATIONS_MESSAGE}
        </p>
      ) : (
        <ul className="divide-y divide-bp-border">
          {stipulations.map((stipulation) => {
            const state = lightReachStipulationState(stipulation)
            return (
              <li
                key={`${stipulation.type}:${stipulation.description}`}
                data-testid={`${testIdPrefix}-lightreach-stipulation`}
                className="flex items-start justify-between gap-3 px-4 py-3 text-[length:var(--step--2)]"
              >
                <span className="text-bp-text">{stipulation.description}</span>
                <span
                  className={clsx(
                    badgeClass,
                    `shrink-0`,
                    OUTCOME_TONE_CLASS[state.tone],
                  )}
                >
                  {state.label}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

function ApplicationDetails({
  testIdPrefix,
  application,
}: {
  testIdPrefix: string
  application: LightReachApplicationView
}): JSX.Element {
  const offer = application.loanProduct?.lightReach ?? null
  return (
    <dl
      data-testid={`${testIdPrefix}-lightreach-application-details`}
      className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-4 gap-y-2 px-4 py-4 text-[length:var(--step--2)] text-bp-text"
    >
      <dt className="text-bp-text-muted">Submitted</dt>
      <dd>{dateLabel(application.createdAt)}</dd>
      <dt className="text-bp-text-muted">Credit expires</dt>
      <dd data-testid={`${testIdPrefix}-lightreach-credit-expiry`}>
        {application.creditExpiresOn != null
          ? calendarDateLabel(application.creditExpiresOn)
          : `Not reported`}
      </dd>
      <dt className="text-bp-text-muted">Offer</dt>
      <dd>
        {offer != null
          ? `${TPO_PRODUCT_TYPE_LABEL[offer.productType]} · ${tpoTermsText(offer)}`
          : `—`}
      </dd>
    </dl>
  )
}

/**
 * LightReach application status: LightReach's status, the contract, the
 * stipulations it reported, and the next step. Status changes arrive through
 * LightReach webhooks on the server; Refresh status re-reads financing, or
 * runs the kind's refresh command when it has one. A kind that lets the
 * seller apply again passes `onReapply`, shown while the read allows it.
 */
export function LightReachFinancingStatus({
  bundle,
  proposalId,
  financing,
  onReapply,
}: {
  bundle: LightReachApplicationBundle
  proposalId: string
  financing: LightReachFinancingView
  onReapply?: () => void
}): JSX.Element {
  const { testIdPrefix } = bundle
  // The drawer already observes the financing read; re-run that query
  // rather than add a second observer that would refetch on mount.
  const queryClient = useQueryClient()
  const financingKey = bundle.financingQueryKey(proposalId)
  const useRefreshCommand = bundle.useRefreshApplication ?? useNoRefreshCommand
  const refreshCommand = useRefreshCommand()
  const refetching = useIsFetching({ queryKey: financingKey, exact: true }) > 0
  const refreshing = refreshCommand?.isLoading ?? refetching
  const [refreshFailed, setRefreshFailed] = useState(false)
  const [application, ...earlier] = lightReachApplications(financing)
  const reapply = financing.actions.reapplyLightReach
  const canReapply = onReapply != null && reapply?.allowed === true

  const refresh = (): void => {
    setRefreshFailed(false)
    if (refreshCommand != null) {
      refreshCommand.mutate(
        { proposalId },
        { onError: () => setRefreshFailed(true) },
      )
      return
    }
    queryClient
      .refetchQueries(
        { queryKey: financingKey, exact: true },
        { throwOnError: true },
      )
      .catch(() => setRefreshFailed(true))
  }

  const refreshAllowed =
    financing.actions.refreshLightReachApplication?.allowed !== false
  const refreshButton = !refreshAllowed ? null : (
    <button
      type="button"
      data-testid={`${testIdPrefix}-lightreach-refresh-status`}
      disabled={refreshing}
      onClick={refresh}
      className="rounded-md border border-bp-border-strong px-3 py-1.5 text-[length:var(--step--2)] font-semibold text-bp-text hover:bg-bp-elevated-2 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {refreshing
        ? LIGHTREACH_REFRESHING_STATUS_LABEL
        : LIGHTREACH_REFRESH_STATUS_LABEL}
    </button>
  )

  if (application == null) {
    const reason = financing.actions.startLightReachApplication?.reason
    return (
      <div
        data-testid={`${testIdPrefix}-lightreach-no-application`}
        className="flex flex-col items-start gap-3 text-[length:var(--step--1)] text-bp-text-muted"
      >
        <p>{LIGHTREACH_NO_APPLICATION_MESSAGE}</p>
        {reason != null ? (
          <p className="text-[length:var(--step--2)]">{reason}</p>
        ) : null}
        {refreshButton}
      </div>
    )
  }

  const outcome = lightReachApplicationOutcome(
    application,
    {
      current: financing.lightReachContract,
      send: financing.actions.sendLightReachContract,
      refresh: financing.actions.refreshLightReachContract,
    },
    {
      // Re-apply, and its reason, only where this kind offers it.
      reapply: onReapply != null ? reapply : undefined,
      reportsExpiry: bundle.reportsExpiry,
      resendAfterClose: bundle.resendAfterClose,
    },
  )

  return (
    <div
      data-testid={`${testIdPrefix}-lightreach-financing-status`}
      className="flex flex-col gap-4"
    >
      <section
        aria-label="Application"
        data-testid={`${testIdPrefix}-lightreach-application-summary`}
        className={sectionClass}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-bp-border px-4 py-3">
          <span
            data-testid={`${testIdPrefix}-lightreach-status-badge`}
            className={clsx(badgeClass, OUTCOME_TONE_CLASS[outcome.tone])}
          >
            {outcome.badge}
          </span>
          <span
            data-testid={`${testIdPrefix}-lightreach-reference`}
            className="text-[length:var(--step--2)] text-bp-text-muted tabular-nums"
          >
            {application.providerReference != null
              ? `LightReach reference ${application.providerReference}`
              : `LightReach reference pending`}
          </span>
        </div>
        <div className="flex flex-col gap-3 px-4 py-4">
          <div>
            <p
              data-testid={`${testIdPrefix}-lightreach-outcome-title`}
              className="text-[length:var(--step-0)] font-semibold text-bp-text"
            >
              {outcome.title}
            </p>
            <p className="mt-1 text-[length:var(--step--1)] text-bp-text-muted">
              {outcome.detail}
            </p>
          </div>
          <p
            data-testid={`${testIdPrefix}-lightreach-next-step`}
            className={clsx(
              `text-[length:var(--step--1)]`,
              outcome.supportNeeded ? `text-bp-gold` : `text-bp-text`,
            )}
          >
            <span className="text-bp-text-muted">
              {LIGHTREACH_NEXT_STEP_LABEL}:
            </span>
            {` `}
            <span className="font-semibold">{outcome.nextStep}</span>
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {refreshButton}
            {canReapply ? (
              <button
                type="button"
                data-testid={`${testIdPrefix}-lightreach-reapply`}
                disabled={refreshing}
                onClick={onReapply}
                className="rounded-md bg-bp-accent px-3 py-1.5 text-[length:var(--step--2)] font-semibold text-bp-on-accent hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {LIGHTREACH_REAPPLY_LABEL}
              </button>
            ) : null}
            {refreshFailed && !refreshing ? (
              <p
                role="alert"
                className="text-[length:var(--step--2)] text-bp-gold"
              >
                {LIGHTREACH_REFRESH_STATUS_ERROR}
              </p>
            ) : null}
          </div>
        </div>
        <div className="border-t border-bp-border">
          <ApplicationDetails
            testIdPrefix={testIdPrefix}
            application={application}
          />
        </div>
      </section>

      <LightReachContract
        bundle={bundle}
        proposalId={proposalId}
        financing={financing}
      />

      <Stipulations testIdPrefix={testIdPrefix} application={application} />

      {earlier.length > 0 ? (
        <section
          data-testid={`${testIdPrefix}-lightreach-previous-applications`}
          className={sectionClass}
        >
          <h3 className="border-b border-bp-border px-4 py-3 text-[length:var(--step--1)] font-semibold text-bp-text">
            Previous applications
          </h3>
          <ul className="divide-y divide-bp-border">
            {earlier.map((previous) => (
              <li
                key={previous.id}
                className="px-4 py-3 text-[length:var(--step--2)] text-bp-text-muted"
              >
                {[
                  lightReachApplicationOutcome(previous).badge,
                  dateLabel(previous.createdAt),
                ].join(` · `)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
